import { useState, useEffect, useCallback, useRef } from "react";
import Spline from "@splinetool/react-spline";
import styled, { keyframes } from "styled-components";

export default function App() {
  const [progress, setProgress] = useState(0);
  const [splineLoaded, setSplineLoaded] = useState(false);
  const [loaderDone, setLoaderDone] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  /* loader */
  useEffect(() => {
    if (progress >= 100) return;
    const target = splineLoaded ? 99 : 90;
    const speed = splineLoaded ? 40 : 80;
    if (progress < target) {
      const t = setTimeout(() => {
        const inc = splineLoaded ? Math.random() * 8 + 3 : Math.random() * 3 + 0.5;
        setProgress((p) => Math.min(p + inc, target));
      }, speed);
      return () => clearTimeout(t);
    }
    if (splineLoaded && progress >= 99 && progress < 100) {
      const t = setTimeout(() => setProgress(100), 1000);
      return () => clearTimeout(t);
    }
  }, [progress, splineLoaded]);

  useEffect(() => {
    if (progress >= 100) {
      const t = setTimeout(() => setLoaderDone(true), 800);
      return () => clearTimeout(t);
    }
  }, [progress]);

  const handleSplineLoad = useCallback(() => setSplineLoaded(true), []);

  /* music */
  const toggleMusic = () => {
    const a = audioRef.current;
    if (!a) return;
    isPlaying ? a.pause() : a.play();
    setIsPlaying(!isPlaying);
  };

  return (
    <Wrapper>
      {!loaderDone && (
        <Loader $hiding={progress >= 100}>
          <LoaderNum>{Math.round(Math.min(progress, 100))}</LoaderNum>
          <LoaderTrack><LoaderFill style={{ width: `${Math.min(progress, 100)}%` }} /></LoaderTrack>
        </Loader>
      )}

      <audio ref={audioRef} src="./sound/music.mp3" loop preload="none" />

      <Spline
        className="spline"
        scene="https://prod.spline.design/vKx0sN6pKjp7UrAK/scene.splinecode"
        onLoad={handleSplineLoad}
      />

      <Overlay>
        {/* nav */}
        <Nav>
          <NavLeft>
            <NavLink href="#gallery">Gallery</NavLink>
            <NavLink href="#interior">Interior</NavLink>
            <NavLink href="#furniture">Furniture</NavLink>
            <NavLink href="#contact">Contact</NavLink>
          </NavLeft>
          <NavRight>
            <BookBtn>Book a Tour</BookBtn>
            <PlayBtn onClick={toggleMusic} $on={isPlaying}>
              {isPlaying ? "Pause" : "Play"}
            </PlayBtn>
          </NavRight>
        </Nav>

        {/* hero */}
        <HeroWrap>
          <Accent />
          <Hero>
            <Tag>Interactive 3D Experience</Tag>
            <Title>
              Experience the<br />
              <Gold>Classic 3D Room</Gold>
            </Title>
            <Desc>
              Step inside a timeless interior design, with elegant furniture and
              warm lighting. Explore the room in 3D, interact with details, and
              feel the atmosphere of a classic lifestyle.
            </Desc>
            <CTA>
              <CTALine />
              Start Exploring
            </CTA>
          </Hero>
        </HeroWrap>
      </Overlay>
    </Wrapper>
  );
}

/* ============ keyframes ============ */

const fadeOut = keyframes`
  to { opacity: 0; }
`;

const up = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const grow = keyframes`
  from { transform: scaleY(0); }
  to   { transform: scaleY(1); }
`;

/* ============ LOADER ============ */

const Loader = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #0e0d0c;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  animation: ${p => p.$hiding ? fadeOut : "none"} 0.8s ease forwards;
`;
const LoaderNum = styled.span`
  font-family: "Georgia", serif;
  font-size: 44px;
  color: #d4c096;
  letter-spacing: 3px;
`;
const LoaderTrack = styled.div`
  width: 180px;
  height: 1px;
  background: rgba(212,192,150,0.12);
  @media (max-width: 480px) { width: 120px; }
`;
const LoaderFill = styled.div`
  height: 100%;
  background: #d4c096;
  transition: width 0.3s ease;
`;

/* ============ WRAPPER ============ */

const Wrapper = styled.div`
  font-family: "Spline Sans", sans-serif;
  color: #f5f5f5;
  position: relative;
  height: 100%;
  overflow: hidden;
  background: linear-gradient(316deg, #7a7272, #141312);

  /* spline canvas overrides */
  canvas {
    width: 900px !important;
    height: 100% !important;
    margin-left: 400px !important;
  }

  .spline {
    position: absolute;
    margin: 0;
    top: 0;
    right: 0;
    opacity: 0;
    animation: splineIn 1.6s ease forwards 0.3s;

    @media (max-width: 1024px) {
      canvas {
        margin-left: 150px !important;
        margin-top: 100px !important;
      }
    }
    @media (max-width: 800px) {
      canvas {
        margin-left: -100px !important;
        margin-top: 120px !important;
      }
    }
    @media (max-width: 600px) {
      canvas {
        margin-left: -200px !important;
        margin-top: 140px !important;
      }
    }
    @media (max-width: 400px) {
      canvas {
        margin-left: -230px !important;
        margin-top: 140px !important;
      }
    }
  }

  @keyframes splineIn {
    from { opacity: 0; transform: scale(1.04); }
    to   { opacity: 1; transform: scale(1); }
  }
`;

/* ============ OVERLAY (all content) ============ */

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  pointer-events: none;
`;

/* ============ NAV ============ */

const Nav = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 28px 40px 28px 100px;
  pointer-events: auto;
  flex-shrink: 0;

  @media (max-width: 1024px) { padding: 20px 24px; }
  @media (max-width: 600px) { padding: 16px 16px; }
`;

const NavLeft = styled.div`
  display: flex;
  gap: 6px;
  @media (max-width: 800px) { display: none; }
`;

const NavLink = styled.a`
  text-decoration: none;
  color: rgba(245,245,245,0.65);
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border-radius: 8px;
  transition: all 0.3s ease;
  letter-spacing: 0.2px;

  &:hover {
    color: #f5f5f5;
    background: rgba(212,192,150,0.08);
  }
`;

const NavRight = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: 800px) {
    margin-left: auto;
  }
`;

const btnBase = `
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.4px;
  padding: 10px 22px;
  border-radius: 10px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  transition: all 0.3s ease;
  pointer-events: auto;
`;

const BookBtn = styled.button`
  ${btnBase}
  background: rgba(212,192,150,0.1);
  border: 1px solid rgba(212,192,150,0.25);
  color: #e4d4b4;

  &:hover {
    background: rgba(212,192,150,0.18);
    border-color: rgba(212,192,150,0.5);
  }
`;

const PlayBtn = styled.button`
  ${btnBase}
  background: ${p => p.$on ? "rgba(212,192,150,0.15)" : "rgba(255,255,255,0.04)"};
  border: 1px solid ${p => p.$on ? "rgba(212,192,150,0.35)" : "rgba(255,255,255,0.08)"};
  color: ${p => p.$on ? "#e4d4b4" : "rgba(245,245,245,0.45)"};

  &:hover {
    background: rgba(212,192,150,0.12);
    border-color: rgba(212,192,150,0.3);
    color: #e4d4b4;
  }
`;

/* ============ HERO ============ */

const HeroWrap = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  padding: 0 40px 0 100px;
  position: relative;

  @media (max-width: 1024px) {
    padding: 0 24px;
    background: linear-gradient(
      to right,
      rgba(14, 13, 12, 0.92) 0%,
      rgba(14, 13, 12, 0.7) 55%,
      transparent 100%
    );
    backdrop-filter: blur(2px);
    pointer-events: auto;
  }

  @media (max-width: 600px) {
    padding: 0 16px;
    background: linear-gradient(
      to right,
      rgba(14, 13, 12, 0.95) 0%,
      rgba(14, 13, 12, 0.75) 60%,
      transparent 100%
    );
    backdrop-filter: blur(3px);
  }
`;

const Accent = styled.div`
  position: absolute;
  left: 60px;
  top: 50%;
  transform: translateY(-50%);
  transform-origin: center;
  width: 1.5px;
  height: 320px;
  background: linear-gradient(180deg, transparent 0%, #d4c096 30%, #8a7556 70%, transparent 100%);
  animation: ${grow} 1.6s ease forwards 0.5s;
  opacity: 0.5;

  @media (max-width: 1024px) { display: none; }
`;

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 520px;
  pointer-events: none;

  @media (max-width: 800px) {
    max-width: 340px;
  }
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: rgba(212,192,150,0.7);
  opacity: 0;
  animation: ${up} 0.8s ease forwards 0.1s;
`;

const Title = styled.h1`
  font-family: "Georgia", serif;
  font-size: 60px;
  font-weight: 700;
  line-height: 1.08;
  margin: 0;
  color: #f5f5f5;
  text-shadow: 0 4px 30px rgba(0,0,0,0.5);
  opacity: 0;
  animation: ${up} 0.8s ease forwards 0.2s;

  @media (max-width: 1024px) { font-size: 46px; }
  @media (max-width: 800px)  { font-size: 32px; }
  @media (max-width: 400px)  { font-size: 28px; }
`;

const Gold = styled.span`
  color: #e4d4b4;
`;

const Desc = styled.p`
  font-size: 14px;
  line-height: 1.75;
  color: rgba(245,245,245,0.45);
  margin: 0;
  max-width: 380px;
  opacity: 0;
  animation: ${up} 0.8s ease forwards 0.35s;

  @media (max-width: 800px) {
    font-size: 13px;
    max-width: 300px;
  }
`;

const CTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: rgba(212,192,150,0.06);
  border: 1px solid rgba(212,192,150,0.18);
  color: #e4d4b4;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.6px;
  padding: 13px 28px;
  border-radius: 10px;
  cursor: pointer;
  pointer-events: auto;
  width: fit-content;
  backdrop-filter: blur(8px);
  transition: all 0.35s ease;
  opacity: 0;
  animation: ${up} 0.8s ease forwards 0.5s;

  &:hover {
    background: rgba(212,192,150,0.12);
    border-color: rgba(212,192,150,0.4);
    transform: translateY(-2px);
    gap: 16px;
  }
`;

const CTALine = styled.span`
  display: block;
  width: 20px;
  height: 1px;
  background: rgba(212,192,150,0.6);
  transition: all 0.35s ease;

  ${CTA}:hover & {
    width: 28px;
    background: #d4c096;
  }
`;
